import React, { useState, cloneElement, useRef, useEffect } from "react"
import { cn } from "../../lib/utils"

export function DropdownMenu({ children }) {
  return <div>{children}</div>
}

export function DropdownMenuTrigger({ children, asChild, onOpen, onClose, triggerRef, ...props }) {
  const Comp = asChild ? React.cloneElement(children, props) : <button ref={triggerRef} {...props}>{children}</button>
  return Comp
}

export function DropdownMenuContent({ className, children, open, setOpen, align = "center", ...props }) {
  const contentRef = useRef(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!open) return

    const handleClickOutside = (event) => {
      if (
        contentRef.current &&
        !contentRef.current.contains(event.target) &&
        !event.target.closest('[data-dropdown-trigger="true"]')
      ) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open, setOpen])

  if (!open) return null

  return (
    <div
      ref={contentRef}
      className={cn(
        "absolute z-[9999] mt-2 min-w-[8rem] overflow-hidden rounded-md border bg-white p-1 text-popover-foreground shadow-md",
        align === "start" && "left-0",
        align === "center" && "left-1/2 -translate-x-1/2",
        align === "end" && "right-0",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export function DropdownMenuLabel({ className, ...props }) {
  return <div className={cn("px-2 py-1.5  font-semibold", className)} {...props} />
}

export function DropdownMenuItem({ className, onClick, setOpen, ...props }) {
  const handleClick = (e) => {
    if (onClick) onClick(e)
    if (setOpen) setOpen(false) // Close the dropdown when an item is clicked
  }

  return (
    <button
      className={cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5  outline-none transition-colors focus:bg-accent focus:text-accent-foreground hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className,
      )}
      onClick={handleClick}
      {...props}
    />
  )
}

export function DropdownMenuSeparator({ className, ...props }) {
  return <div className={cn("-mx-1 my-1 h-px bg-muted", className)} {...props} />
}

export function DropdownMenuGroup({ className, ...props }) {
  return <div className={cn("", className)} {...props} />
}

// HOC to wrap DropdownMenuTrigger and DropdownMenuContent with state
export function DropdownMenuWrapper({ children }) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef(null)
  let trigger = null
  let content = null
  let items = []

  React.Children.forEach(children, child => {
    if (!child) return
    if (child.type && child.type.name === 'DropdownMenuTrigger') {
      trigger = cloneElement(child, {
        onClick: (e) => {
          e.stopPropagation()
          setOpen(!open)
          if (child.props.onClick) child.props.onClick(e)
        },
        triggerRef,
        "data-dropdown-trigger": "true",
      })
    } else if (child.type && child.type.name === 'DropdownMenuContent') {
      content = cloneElement(child, {
        open,
        setOpen
      })
    } else {
      items.push(child)
    }
  })

  // Add setOpen to all DropdownMenuItems
  if (content && content.props.children) {
    const addSetOpenToItems = (children) => {
      return React.Children.map(children, child => {
        if (!child || typeof child !== 'object') return child

        if (child.type && child.type.name === 'DropdownMenuItem') {
          return cloneElement(child, { setOpen })
        }

        if (child.props && child.props.children) {
          return cloneElement(child, {
            children: addSetOpenToItems(child.props.children)
          })
        }

        return child
      })
    }

    content = cloneElement(content, {
      children: addSetOpenToItems(content.props.children)
    })
  }

  // Wrap in a relative div to contain the dropdown menu
  return (
    <div className="relative inline-block">
      {trigger}
      {content}
      {items}
    </div>
  )
}
